import { Component, Injectable, OnDestroy, ViewContainerRef } from "@angular/core";
import { UntypedFormArray, UntypedFormBuilder, UntypedFormGroup, Validators } from "@angular/forms";
import { LgTranslateService, useTranslationNamespace } from "@logex/framework/lg-localization";
import {
    getDialogFactoryBase,
    LgConfirmationDialog,
    LgDialogFactory,
    LgDialogRef
} from "@logex/framework/ui-core";
import { LgMultiFilterSourceValue } from "@logex/framework/ui-core/controls/lg-multi-filter/lg-multi-filter-popup.component";
import { atNextFrame } from "@logex/framework/utilities";
import { OrganizationGateway } from "app/shared/gateways";
import { AppDefinitions, PermissionService } from "app/shared/services";
import {
    AffiliationRoleCode,
    OrganizationAffiliation,
    OrganizationAffiliationMember,
    SearchOrganization
} from "app/shared/types";
import { uniqueAffiliationNameValidator } from "app/shared/validators";

import { Dictionary, isEqual, keyBy } from "lodash";
import { Subject } from "rxjs";
import { distinctUntilChanged, finalize, take, takeUntil } from "rxjs/operators";

@Component({
    selector: "app-affiliation-edit-dialog",
    templateUrl: "./affiliation-edit-dialog.component.html",
    styleUrls: ["./affiliation-edit-dialog.component.scss"],
    providers: [useTranslationNamespace("APP._Dialogs.OrganizationAffiliationEditDialog")]
})
export class AffiliationEditDialogComponent implements OnDestroy {
    _title: string;
    _dialogClass = "lg-dialog lg-dialog--4col";

    _isNew: boolean;
    _affiliation: OrganizationAffiliation;

    _loading = true;
    _saving = false;

    _form: UntypedFormGroup;
    _initFormValue: OrganizationAffiliation;

    _organizations: SearchOrganization[] = [];
    _organizationsDictionary: Dictionary<SearchOrganization> = {};

    _leader: OrganizationAffiliationMember;
    _leaderDropdownDefinition: any;

    _membersSource: LgMultiFilterSourceValue;
    _selectedMembers: { $empty: true };
    _deletedMember: Dictionary<UntypedFormGroup> = {};
    _readonlyMembers: Dictionary<OrganizationAffiliationMember> = {};

    _members: OrganizationAffiliationMember[];

    private _destroyed$ = new Subject<void>();

    constructor(
        private _dialogRef: LgDialogRef<AffiliationEditDialogComponent>,
        private _fb: UntypedFormBuilder,
        private _lgTranslate: LgTranslateService,
        private _gateway: OrganizationGateway,
        private _confirmDialog: LgConfirmationDialog,
        private _permissions: PermissionService,
        public _definitions: AppDefinitions,
        _viewContainerRef: ViewContainerRef
    ) {
        this._confirmDialog = this._confirmDialog.bindViewContainerRef(_viewContainerRef);
    }

    ngOnDestroy(): void {
        this._destroyed$.next();
        this._destroyed$.complete();
    }

    show(affiliation?: OrganizationAffiliation): Promise<boolean> {
        this._isNew = !affiliation;
        this._affiliation = affiliation;

        const leaderIndex = affiliation?.affiliationMembership.findIndex(
            x => x.affiliationRoleCode === AffiliationRoleCode.Leader
        );
        if (leaderIndex > -1) {
            this._leader = this._affiliation.affiliationMembership[leaderIndex];
            this._affiliation.affiliationMembership.splice(leaderIndex, 1);
        }

        if (!this._isNew && !this._permissions.isAdmin) {
            this._members = [];
            this._readonlyMembers = this._affiliation.affiliationMembership
                ? keyBy(this._affiliation.affiliationMembership, x => x.organizationId)
                : {};
        } else {
            this._members = this._affiliation?.affiliationMembership || [];
            this._readonlyMembers = {};
        }

        this._setTitle();
        this._load();

        return this.createPromise();
    }

    _close(): void {
        this._dialogRef.close();
    }

    _cancel(): void {
        this._tryClose();
    }

    _tryClose(): boolean {
        if (this._saving) return;

        if (isEqual(this._initFormValue, this._form.value)) {
            this._close();
            return;
        }

        this._confirmDialog
            .confirmLC(".ConfirmClose", ".ConfirmDiscardingUnsavedData")
            .then(response => {
                if (response === 1) {
                    this._close();
                }
            });
    }

    _confirm(): void {
        if (this._form.invalid) return;

        this._save();
    }

    _memberSelected(e: any): void {
        if (e.$empty) return;

        Object.keys(e).forEach(newMemberId => {
            if (this._deletedMember[newMemberId]) {
                this._revertDelete(newMemberId);
            } else {
                this.members.push(this._memberFormGroup(Number(newMemberId)));
            }
        });

        this._setDropdownDefinitions();
    }

    get members(): UntypedFormArray {
        return this._form.get("affiliationMembership") as UntypedFormArray;
    }

    _deleteMember(index: number): void {
        const deleted = this.members.at(index) as UntypedFormGroup;

        if (deleted.value.id) {
            deleted.get("isDeleted").setValue(true);
            this._deletedMember[deleted.value.organizationId] = deleted;
        } else {
            this.members.removeAt(index);
        }

        this._setDropdownDefinitions();
    }

    _clearAll(): void {
        this._confirmDialog
            .confirmLC(".ClearAll.ConfirmTitle", ".ClearAll.ConfirmText")
            .then(choice => {
                if (choice === 1) {
                    this.members.controls.forEach((control: UntypedFormGroup) => {
                        control.get("isDeleted").setValue(true);
                        this._deletedMember[control.value.organizationId] = control;
                    });
                    this._save();
                }
            });
    }

    private _save(): void {
        this._saving = true;

        const leaderId = this._form.value.leaderId;
        const affiliation = this._form.value as OrganizationAffiliation;

        // set leader
        if (this._leader) {
            if (leaderId == null) {
                this._leader.isDeleted = true;
            } else if (this._leader.organizationId !== leaderId) {
                this._leader.isDeleted = false;
                this._leader.organizationId = leaderId;
            }
            affiliation.affiliationMembership.push(this._leader);
        } else if (leaderId != null) {
            affiliation.affiliationMembership.push({
                organizationId: leaderId,
                affiliationRoleCode: AffiliationRoleCode.Leader,
                activeFrom: null,
                activeTo: null,
                isDeleted: false
            });
        }

        const request = this._isNew
            ? this._gateway.createAffiliation(affiliation)
            : this._gateway.updateAffiliation(affiliation);

        request
            .pipe(
                takeUntil(this._destroyed$),
                take(1),
                finalize(() => (this._saving = false))
            )
            .subscribe(
                () => {
                    this._resolve(true);
                    this._close();
                },
                () => {
                    this._confirmDialog.alertLC(".Error.Title", ".Error.Text");
                }
            );
    }

    private _setTitle(): void {
        this._title = this._lgTranslate.translate(
            this._isNew ? ".AddAffiliation" : ".EditAffiliation"
        );
    }

    private _load(): void {
        this._gateway
            .organizationSearch()
            .pipe(
                takeUntil(this._destroyed$),
                finalize(() => (this._loading = false))
            )
            .subscribe(organizations => {
                this._organizations = organizations;
                this._organizationsDictionary = keyBy(this._organizations, x => x.id);
                this._initForm();
                this._setDropdownDefinitions();
                atNextFrame(() => this._dialogRef.center());
            });
    }

    private _revertDelete(organizationId: string): void {
        const deleted = this._deletedMember[organizationId];
        deleted.get("isDeleted").setValue(false);
        delete this._deletedMember[organizationId];
    }

    private _setDropdownDefinitions(): void {
        const selected = this._form
            .getRawValue()
            .affiliationMembership.reduce(
                (acc, x: OrganizationAffiliationMember) =>
                    x.isDeleted ? acc : { ...acc, [x.organizationId]: x },
                {}
            );
        const leaderId = this._form.getRawValue().leaderId;

        this._leaderDropdownDefinition = {
            groups: [
                {
                    entries: this._organizations.filter(
                        x => !selected[x.id] && !this._readonlyMembers[x.id]
                    )
                }
            ],
            entryName: "name",
            entryId: "id"
        };

        this._membersSource = this._organizations
            .filter(x => x.id != leaderId && !selected[x.id] && !this._readonlyMembers[x.id])
            .map(organization => {
                return {
                    id: organization.id,
                    name: organization.name,
                    order: organization.id
                };
            });
        this._selectedMembers = { $empty: true };
    }

    private _initForm(): void {
        this._form = this._fb.group({
            id: [this._affiliation?.id],
            displayName: [
                this._affiliation?.displayName,
                Validators.required,
                uniqueAffiliationNameValidator(this._gateway, this._affiliation?.displayName)
            ],
            leaderId: [this._leader?.organizationId],
            affiliationMembership: new UntypedFormArray(
                this._members.map(x => this._memberFormGroup(x.organizationId, x))
            )
        });

        this._form
            .get("leaderId")
            .valueChanges.pipe(distinctUntilChanged(), takeUntil(this._destroyed$))
            .subscribe(() => {
                this._setDropdownDefinitions();
            });

        this._initFormValue = this._form.value;
    }

    private _memberFormGroup(
        organizationId: number,
        member?: OrganizationAffiliationMember
    ): UntypedFormGroup {
        const group = this._fb.group({
            id: [member?.id],
            organizationId: [organizationId, Validators.required],
            affiliationRoleCode: [AffiliationRoleCode.Participant, Validators.required],

            activeFrom: [member?.activeFrom],
            activeTo: [member?.activeTo],

            isDeleted: [!!member?.isDeleted]
        });

        return group;
    }

    private _resolve: (result: boolean) => void;
    private _reject: (reason?: any) => void;

    private createPromise(): Promise<boolean> {
        return new Promise<boolean>((resolve, reject) => {
            this._resolve = resolve;
            this._reject = reject;
        });
    }
}

// ----------------------------------------------------------------------------------
//
@Injectable()
export class AffiliationEditDialog extends getDialogFactoryBase(
    AffiliationEditDialogComponent,
    "show"
) {
    constructor(_factory: LgDialogFactory) {
        super(_factory);
    }
}
